import type {
  ContractFamilyMetadataRequest,
  SupplierContactMode,
  SupplierDivision,
  SupplierFamilySummary,
} from "@/types/supplier";

export interface ContractFamilyMetadataValues {
  name: string;
  external_id: string;
  owner_user_id: string;
  estimated_total_contract_value: string;
  estimated_annual_contract_value: string;
  currency_code: string;
  procurement_status: string;
  expiry_date: string;
  divisions: SupplierDivision[];
  contact_mode: SupplierContactMode;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
}

const emptyValues: ContractFamilyMetadataValues = {
  name: "",
  external_id: "",
  owner_user_id: "",
  estimated_total_contract_value: "",
  estimated_annual_contract_value: "",
  currency_code: "",
  procurement_status: "",
  expiry_date: "",
  divisions: [],
  contact_mode: "inherited",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
};

// Financial fields retain manual values. Status is always manual. The end date
// shows the effective value and is sent only when the user changes it.
const manualValue = <T>(manual: T | undefined, effective: T): T => (manual === undefined ? effective : manual);

export const familyMetadataValues = (family?: SupplierFamilySummary): ContractFamilyMetadataValues =>
  family
    ? {
        name: family.name,
        external_id: family.external_id ?? "",
        owner_user_id: family.owner_inherited ? "" : (family.owner_user_id ?? ""),
        estimated_total_contract_value:
          manualValue(family.manual_estimated_total_contract_value, family.estimated_total_contract_value) ?? "",
        estimated_annual_contract_value:
          manualValue(family.manual_estimated_annual_contract_value, family.estimated_annual_contract_value) ?? "",
        currency_code: manualValue(family.manual_currency_code, family.currency_code) ?? "",
        procurement_status: family.procurement_status ?? "",
        expiry_date: family.expiry_date ?? "",
        divisions: family.divisions ?? [],
        contact_mode: family.contact_mode,
        contact_name: family.contact_mode === "custom" ? (family.contact_name ?? "") : "",
        contact_email: family.contact_mode === "custom" ? (family.contact_email ?? "") : "",
        contact_phone: family.contact_mode === "custom" ? (family.contact_phone ?? "") : "",
      }
    : emptyValues;

export const contractFolderFsEntryId = (folder: SupplierFamilySummary) => folder.fs_entry_id ?? folder.id;

const nullable = (value: string) => value.trim() || null;

export const toContractFamilyMetadataRequest = (
  values: ContractFamilyMetadataValues,
  initialExpiryDate = "",
): ContractFamilyMetadataRequest => ({
  name: values.name.trim() || undefined,
  external_id: nullable(values.external_id),
  owner_user_id: nullable(values.owner_user_id),
  estimated_total_contract_value: nullable(values.estimated_total_contract_value),
  estimated_annual_contract_value: nullable(values.estimated_annual_contract_value),
  currency_code: nullable(values.currency_code),
  procurement_status: (values.procurement_status || null) as ContractFamilyMetadataRequest["procurement_status"],
  // An empty end date reverts to the Contract Summary date.
  ...(values.expiry_date !== initialExpiryDate ? { expiry_date: nullable(values.expiry_date) } : {}),
  divisions: values.divisions,
  contact_mode: values.contact_mode,
  contact_name: values.contact_mode === "custom" ? nullable(values.contact_name) : null,
  contact_email: values.contact_mode === "custom" ? nullable(values.contact_email) : null,
  contact_phone: values.contact_mode === "custom" ? nullable(values.contact_phone) : null,
});
